import React, { useEffect, useRef, useState } from 'react';
import './TranslationTooltip.css';

interface TranslationTooltipProps {
  word: string;
  translation: string;
  phonetic: string | null;
  showPhonetics: boolean;
  saved: boolean;
  fontSize?: 'small' | 'large';
  canShowTooltip?: boolean;
  tooltipId: string; // Unique ID for this tooltip instance
  activeTooltipId: string | null; // ID of the currently active tooltip (managed by parent)
  onOpen: (tooltipId: string | null) => void; // Callback to set the active tooltip
  isControlled: boolean; // Is this component controlled by parent state?
  children: React.ReactNode;
}

const TranslationTooltip: React.FC<TranslationTooltipProps> = ({
  word,
  translation,
  phonetic,
  showPhonetics,
  saved,
  fontSize = 'large',
  canShowTooltip = true,
  tooltipId,
  activeTooltipId,
  onOpen,
  isControlled,
  children,
}) => {
  // Internal state for uncontrolled mode
  const [internalVisible, setInternalVisible] = useState(false);

  // Determine visibility based on mode
  const controlledVisible = activeTooltipId === tooltipId;
  const visibleState = isControlled ? controlledVisible : internalVisible;
  const isVisible = visibleState && canShowTooltip;

  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipContentRef = useRef<HTMLSpanElement>(null);
  const tooltipPopupRef = useRef<HTMLDivElement>(null);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && tooltipContentRef.current) {
      const rect = tooltipContentRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top,
        left: rect.left + rect.width / 2,
      });
    }
  }, [isVisible]);

  // Effect to handle clicks outside the tooltip popup
  useEffect(() => {
    if (!isVisible) {
      return; // Don't add listener if not visible
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipPopupRef.current &&
        !tooltipPopupRef.current.contains(event.target as Node)
      ) {
        if (
          tooltipContentRef.current &&
          !tooltipContentRef.current.contains(event.target as Node)
        ) {
          // Close based on mode
          if (isControlled) {
            onOpen(null);
          } else {
            setInternalVisible(false);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // Add isControlled and onOpen to dependencies
  }, [isVisible, isControlled, onOpen]);

  // Handle click on the trigger word span
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isControlled) {
      const newActiveId = activeTooltipId === tooltipId ? null : tooltipId;
      onOpen(newActiveId);
    } else {
      // Toggle internal state, but only allow showing if canShowTooltip is true
      const nextVisible = !internalVisible;
      if (nextVisible && !canShowTooltip) {
        // Do nothing if trying to show when not allowed
      } else {
        setInternalVisible(nextVisible);
      }
    }
  };

  return (
    // Use span for inline elements, but div might be needed if it causes layout issues
    <span className="tooltip-container" onClick={handleClick}>
      {isVisible && (
        <div
          className="tooltip"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
          ref={tooltipPopupRef}
        >
          <div className="tooltip-words">
            {phonetic && <span className="tooltip-phonetic">{phonetic}</span>}
            <span className="tooltip-word">{word}</span>
            <span className="tooltip-translation">{translation}</span>
          </div>
        </div>
      )}
      <span className="tooltip-content" ref={tooltipContentRef}>
        <span
          className={`tooltip-content-phonetic ${fontSize}`}
          style={{ visibility: showPhonetics ? 'visible' : 'hidden' }}
        >
          {phonetic}
        </span>
        <span
          className={`tooltip-content-word ${isVisible ? 'highlight' : ''} ${fontSize}`}
        >
          {children}
        </span>
      </span>
    </span>
  );
};

export default TranslationTooltip;
